import React from "react";
import { Link, useParams } from "@tanstack/react-router";
import type { GetShowQuery } from "@/codegen/graphql/generated/graphql";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface EpisodeBreadcrumbProps extends React.ComponentProps<"nav"> {
  show: GetShowQuery["show"];
  episodes: GetShowQuery["show"]["episodes"];
}

export const EpisodeBreadcrumbSkeleton: React.FC<
  React.ComponentProps<"nav">
> = ({ className, ...props }) => {
  return (
    <Breadcrumb className={cn(className)} {...props}>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Skeleton className="h-2 w-24" />
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Skeleton className="h-2 w-24" />
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Skeleton className="h-2 w-24" />
          </BreadcrumbLink>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const EpisodeBreadcrumb: React.FC<EpisodeBreadcrumbProps> = ({
  show,
  episodes,
  className,
  ...props
}) => {
  const { episode, series } = useParams({ strict: false });
  const episodeId = episode === "0" ? episodes[0]?.id : episode;
  const episodeNumber = episodes.find((e) => e.id === episodeId)?.number;

  return (
    <Breadcrumb className={cn(className)} {...props}>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          {/* The series details page (SHO-757), reusing the segment the
              visitor arrived on, like the address bar does. */}
          <BreadcrumbLink asChild>
            <Link
              to="/series/$series"
              params={{ series: series ?? "" }}
              state={{ seriesDetailsOrigin: "player breadcrumb" }}
            >
              {show.displayTitle}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>
            {episodeNumber && episodeNumber !== 0
              ? `Episode ${episodes.find((e) => e.id === episodeId)?.number}`
              : "Trailer"}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default EpisodeBreadcrumb;
